import React, { useCallback, useEffect, useRef, useState } from 'react'
import swal from 'sweetalert'

import { getPaper, submitPaper } from './api'
import BackButton from '../../components/backButton'
import { loaderStart, loaderEnd } from '../../components/loader'
import '../Question-bank/questionbank.css'
import './styles.css'

/**
 * Practice test: a timed mock of the Computer Competency Test — 100 questions,
 * one mark each, 90 minutes — drawn at random from the real question pool.
 * Public — no login needed.
 *
 * The answers never reach the browser before submission: the paper arrives
 * without them, the server records it as an attempt, then scores the
 * submission against that recorded paper and returns the correct option for
 * each question so the candidate can review it.
 */

const PAPER = { questions: 100, marks: 100, minutes: 90 }

const OPTION_KEYS = ['opt1', 'opt2', 'opt3', 'opt4']
const OPTION_LETTERS = ['A', 'B', 'C', 'D']

const formatClock = (seconds) => {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const PracticeTest = () => {
  // 'start' -> 'test' -> 'result'
  const [stage, setStage] = useState('start')
  const [attemptId, setAttemptId] = useState(null)
  const [questions, setQuestions] = useState([])
  const [answers, setAnswers] = useState({})
  const [current, setCurrent] = useState(0)
  const [secondsLeft, setSecondsLeft] = useState(0)
  const [result, setResult] = useState(null)
  const [reviewFilter, setReviewFilter] = useState('all')
  const startedAt = useRef(null)
  const submitting = useRef(false)

  const start = async () => {
    loaderStart()
    const res = await getPaper()
    loaderEnd()
    if (res?.code !== 1 || !Array.isArray(res.data?.questions) || res.data.questions.length === 0) {
      swal('Could not load the practice paper', res?.message || 'Please try again shortly.', 'error')
      return
    }
    setAttemptId(res.data.attemptId)
    setQuestions(res.data.questions)
    setAnswers({})
    setCurrent(0)
    setResult(null)
    setReviewFilter('all')
    // The server sets the time limit, so the clock and its expiry check agree.
    setSecondsLeft((Number(res.data.durationMinutes) || PAPER.minutes) * 60)
    startedAt.current = Date.now()
    submitting.current = false
    setStage('test')
    window.scrollTo(0, 0)
  }

  const submit = useCallback(async () => {
    if (submitting.current) return
    submitting.current = true
    loaderStart()
    const res = await submitPaper(
      attemptId,
      questions.map((q) => ({ id: q.id, answer: answers[q.id] ?? null }))
    )
    loaderEnd()
    if (res?.code !== 1) {
      submitting.current = false
      swal('Could not submit the paper', res?.message || 'Please check your connection and try again.', 'error')
      return
    }
    const byId = new Map(res.data.results.map((r) => [Number(r.id), r]))
    setResult({
      ...res.data,
      seconds: Math.round((Date.now() - startedAt.current) / 1000),
      byId,
    })
    setStage('result')
    window.scrollTo(0, 0)
  }, [attemptId, questions, answers])

  // Countdown. Submits automatically when time runs out, as the real exam does.
  useEffect(() => {
    if (stage !== 'test') return
    if (secondsLeft <= 0) {
      swal("Time's up", 'Your paper has been submitted.', 'info')
      submit()
      return
    }
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000)
    return () => clearTimeout(timer)
  }, [stage, secondsLeft, submit])

  // Leaving mid-test loses the attempt, so ask first.
  useEffect(() => {
    if (stage !== 'test') return
    const warn = (e) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [stage])

  const confirmSubmit = async () => {
    const unanswered = questions.length - Object.keys(answers).length
    const ok = await swal({
      title: 'Submit your paper?',
      text: unanswered > 0
        ? `You have ${unanswered} unanswered question${unanswered === 1 ? '' : 's'}.`
        : 'You have answered every question.',
      icon: 'warning',
      buttons: ['Keep going', 'Submit'],
    })
    if (ok) submit()
  }

  const choose = (questionId, option) => {
    setAnswers((prev) => ({ ...prev, [questionId]: option }))
  }

  const clearAnswer = (questionId) => {
    setAnswers((prev) => {
      const next = { ...prev }
      delete next[questionId]
      return next
    })
  }

  // --- Start ---------------------------------------------------------------

  if (stage === 'start') {
    return (
      <div className='qb_page'>
        <BackButton to='/' />
        <div className='qb_header'>
          <h1 className='qb_title'>Practice Test</h1>
          <p className='qb_subtitle'>
            Take a full mock of the Computer Competency Test, with questions drawn
            from the real question pool. It does not count towards your exam —
            practise as often as you like.
          </p>
        </div>

        <div className='ui_panel pt_start'>
          <div className='ui_panel_head'>
            <h2 className='ui_panel_title'>Mock Examination</h2>
          </div>
          <div className='ui_panel_body'>
            <div className='pt_facts'>
              <div className='pt_fact'>
                <span className='pt_fact_value'>{PAPER.questions}</span>
                <span className='pt_fact_label'>Questions</span>
              </div>
              <div className='pt_fact'>
                <span className='pt_fact_value'>{PAPER.marks}</span>
                <span className='pt_fact_label'>Marks</span>
              </div>
              <div className='pt_fact'>
                <span className='pt_fact_value'>{PAPER.minutes}</span>
                <span className='pt_fact_label'>Minutes</span>
              </div>
            </div>

            <h3 className='pt_rules_title'>Instructions / निर्देश</h3>
            <ul className='pt_rules'>
              <li>The paper has {PAPER.questions} questions of one mark each, to be completed in {PAPER.minutes} minutes.</li>
              <li>Each question has four options and one correct answer.</li>
              <li>प्रत्येक प्रश्न के चार विकल्प हैं, जिनमें से एक सही उत्तर है।</li>
              <li>There is no negative marking. You can change or clear an answer until you submit.</li>
              <li>The paper is submitted automatically when the time runs out.</li>
              <li>After submitting you can review every question with its correct answer.</li>
            </ul>

            <button type='button' className='ui_btn ui_btn_primary ui_btn_block' onClick={start}>
              <i className='fa fa-play' aria-hidden='true'></i> Start Test
            </button>
          </div>
        </div>
      </div>
    )
  }

  // --- Test ----------------------------------------------------------------

  if (stage === 'test') {
    const q = questions[current]
    const answeredCount = Object.keys(answers).length
    const lowTime = secondsLeft <= 60

    return (
      <div className='qb_page'>
        <div className='pt_bar'>
          <div className='pt_bar_title'>
            Practice Test
            <span className='pt_bar_sub'>
              {answeredCount} of {questions.length} answered
            </span>
          </div>
          <div className={`pt_clock ${lowTime ? 'is_low' : ''}`} role='timer' aria-live='off'>
            <i className='fa fa-clock' aria-hidden='true'></i> {formatClock(secondsLeft)}
          </div>
          <button type='button' className='ui_btn ui_btn_primary' onClick={confirmSubmit}>
            Submit
          </button>
        </div>

        <div className='pt_layout'>
          <div className='ui_panel pt_question'>
            <div className='ui_panel_body'>
              <p className='pt_q_no'>Question {current + 1} of {questions.length}</p>
              <p className='pt_q_text'>{q.ques}</p>

              <div className='pt_options' role='radiogroup' aria-label={`Question ${current + 1}`}>
                {OPTION_KEYS.map((key, i) => {
                  const value = i + 1
                  const selected = answers[q.id] === value
                  return (
                    <label key={key} className={`pt_option ${selected ? 'is_selected' : ''}`}>
                      <input
                        type='radio'
                        name={`q_${q.id}`}
                        checked={selected}
                        onChange={() => choose(q.id, value)}
                      />
                      <span className='pt_option_letter'>{OPTION_LETTERS[i]}</span>
                      <span className='pt_option_text'>{q[key]}</span>
                    </label>
                  )
                })}
              </div>

              <div className='pt_nav'>
                <button
                  type='button'
                  className='ui_btn ui_btn_secondary'
                  onClick={() => setCurrent((c) => c - 1)}
                  disabled={current === 0}
                >
                  <i className='fa fa-arrow-left' aria-hidden='true'></i> Previous
                </button>
                <button
                  type='button'
                  className='ui_btn ui_btn_secondary'
                  onClick={() => clearAnswer(q.id)}
                  disabled={answers[q.id] === undefined}
                >
                  Clear
                </button>
                {current < questions.length - 1 ? (
                  <button type='button' className='ui_btn ui_btn_primary' onClick={() => setCurrent((c) => c + 1)}>
                    Next <i className='fa fa-arrow-right' aria-hidden='true'></i>
                  </button>
                ) : (
                  <button type='button' className='ui_btn ui_btn_primary' onClick={confirmSubmit}>
                    Submit
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className='ui_panel pt_palette'>
            <div className='ui_panel_body'>
              <p className='pt_palette_title'>Questions</p>
              <div className='pt_palette_grid'>
                {questions.map((item, i) => (
                  <button
                    type='button'
                    key={item.id}
                    className={`pt_cell ${answers[item.id] !== undefined ? 'is_answered' : ''} ${i === current ? 'is_current' : ''}`}
                    onClick={() => setCurrent(i)}
                    aria-label={`Question ${i + 1}${answers[item.id] !== undefined ? ', answered' : ''}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <div className='pt_legend'>
                <span><i className='pt_dot is_answered'></i> Answered</span>
                <span><i className='pt_dot'></i> Not answered</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // --- Result --------------------------------------------------------------

  const skipped = result.total - result.attempted
  const percent = result.total ? Math.round((result.correct / result.total) * 100) : 0

  const reviewQuestions = questions
    .map((q, i) => ({ q, i, r: result.byId.get(Number(q.id)) }))
    .filter(({ r }) => {
      if (!r) return false
      if (reviewFilter === 'wrong') return r.answer !== null && !r.isCorrect
      if (reviewFilter === 'skipped') return r.answer === null
      return true
    })

  return (
    <div className='qb_page'>
      <BackButton to='/' />
      <div className='qb_header'>
        <h1 className='qb_title'>Your Result</h1>
        <p className='qb_subtitle'>
          Mock examination of {result.total} questions · completed in {formatClock(result.seconds)}
        </p>
      </div>

      <div className='pt_summary'>
        <div className='pt_stat pt_stat_main'>
          <span className='pt_stat_value'>{result.correct}<small>/{result.total}</small></span>
          <span className='pt_stat_label'>Marks · {percent}%</span>
        </div>
        <div className='pt_stat'>
          <span className='pt_stat_value pt_good'>{result.correct}</span>
          <span className='pt_stat_label'>Correct</span>
        </div>
        <div className='pt_stat'>
          <span className='pt_stat_value pt_bad'>{result.wrong}</span>
          <span className='pt_stat_label'>Wrong</span>
        </div>
        <div className='pt_stat'>
          <span className='pt_stat_value'>{skipped}</span>
          <span className='pt_stat_label'>Not answered</span>
        </div>
      </div>

      <div className='pt_actions'>
        <button type='button' className='ui_btn ui_btn_primary' onClick={() => setStage('start')}>
          <i className='fa fa-redo' aria-hidden='true'></i> Take another test
        </button>
      </div>

      <h2 className='qb_section_label'>
        Review
        <span className='qb_count'>{reviewQuestions.length} shown</span>
      </h2>

      <div className='pt_filters' role='tablist'>
        {[
          ['all', `All (${result.total})`],
          ['wrong', `Wrong (${result.wrong})`],
          ['skipped', `Not answered (${skipped})`],
        ].map(([key, label]) => (
          <button
            type='button'
            key={key}
            role='tab'
            aria-selected={reviewFilter === key}
            className={`pt_filter ${reviewFilter === key ? 'is_active' : ''}`}
            onClick={() => setReviewFilter(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <ol className='pt_review'>
        {reviewQuestions.map(({ q, i, r }) => (
          <li key={q.id} className='ui_panel pt_review_item'>
            <div className='ui_panel_body'>
              <div className='pt_review_head'>
                <span className='pt_q_no'>Question {i + 1}</span>
                {r.answer === null
                  ? <span className='ui_badge ui_badge_wait'>Not answered</span>
                  : r.isCorrect
                    ? <span className='ui_badge ui_badge_pass'>Correct</span>
                    : <span className='ui_badge ui_badge_fail'>Wrong</span>}
              </div>
              <p className='pt_q_text'>{q.ques}</p>
              <ul className='pt_review_options'>
                {OPTION_KEYS.map((key, j) => {
                  const value = j + 1
                  const isAnswer = r.correct === value
                  const isChosen = r.answer === value
                  return (
                    <li
                      key={key}
                      className={`pt_review_option ${isAnswer ? 'is_correct' : ''} ${isChosen && !isAnswer ? 'is_wrong' : ''}`}
                    >
                      <span className='pt_option_letter'>{OPTION_LETTERS[j]}</span>
                      <span className='pt_option_text'>{q[key]}</span>
                      {isAnswer ? (
                        <span className='pt_review_tag'>
                          <i className='fa fa-check' aria-hidden='true'></i> Correct answer
                        </span>
                      ) : isChosen ? (
                        <span className='pt_review_tag'>
                          <i className='fa fa-times' aria-hidden='true'></i> Your answer
                        </span>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}

export default PracticeTest
